<template>
  <div
    v-if="shouldShowStatus"
    class="column"
  >
    <div class="row q-mb-xs">
      <span
        :class="
          props.row.last_submission_at && props.value.value === enums.requestStatus.DRAFT
            ? 'text-bold text-negative'
            : ''
        "
      >
        {{ statusLabel }}
      </span>
      <re-tooltip
        v-if="props.row.last_submission_at && props.value.value === enums.requestStatus.DRAFT"
        :label="$t('monolith.dashboard.review_tooltip')"
        is-error
      />
    </div>

    <div class="row q-gutter-xs">
      <q-badge
        v-for="badge in visibleBadges"
        :key="badge.key"
        :color="badge.color"
        :multi-line="badge.multiLine"
      >
        {{ badge.text }}
      </q-badge>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCurUser } from '@/composables/useCurUser'
import enums from '@/plugins/enums'

// don't automatically insert attrs in root div:
defineOptions({ inheritAttrs: false })

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  value: {
    type: Object,
    required: true,
  },
})

const { hasRole } = useCurUser()
const { t } = useI18n()

const badges = computed(() => props.value.badges)
const isTypeDispatcher = computed(() => hasRole('dispatcher', props.row.valuation_type))
const isValuer = computed(() => props.row.valuer?.is_self)
const isOwner = computed(() => props.row.owner?.is_self)

const renovationCostInK = computed(() => {
  const renovationCost = props.row.features.non_energetic_renovation_cost ?? 0
  const energeticRenovationCost = props.row.features.energetic_renovation_cost ?? 0
  return Math.round((renovationCost + energeticRenovationCost) / 1000)
})

const shouldShowStatus = computed(() => {
  return props.row.requested_valuation_type !== enums.valuationTypes.CAT
})

const statusLabel = computed(() => {
  if (
    ['ers', 'oepc'].includes(props.row.valuation_type) &&
    props.value.value === enums.requestStatus.VALUED
  ) {
    return t('status.completed')
  }

  if (
    props.row.valuation_type === 'ovm' &&
    props.value.value === enums.requestStatus.VALUED &&
    !props.row.report_ref
  ) {
    return t('status.report_generation_pending')
  }
  return props.value.label
})

const visibleBadges = computed(() => {
  const badgeConfigs = [
    {
      key: enums.badgesStatus.CUSTOM_QUOTE_REQUIRED,
      condition: isTypeDispatcher.value || isValuer.value || isOwner.value,
      color: enums.feedbackType.WARNING,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.REFUND_NEEDED,
      condition: isTypeDispatcher.value,
      color: enums.feedbackType.WARNING,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.REPORT_NEEDS_OWNER_APPROVAL,
      condition: true,
      color: enums.feedbackType.INFO,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.REPORT_GENERATION_NEEDED,
      condition: isTypeDispatcher.value,
      color: enums.feedbackType.INFO,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.RESUBMITTED,
      condition: isTypeDispatcher.value || isValuer.value,
      color: enums.feedbackType.WARNING,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.HAS_RENOVATION,
      condition: isTypeDispatcher.value || isValuer.value,
      color: enums.feedbackType.WARNING,
      multiLine: true,
      customText: `reno ${renovationCostInK.value}k€`,
    },
    {
      key: enums.badgesStatus.NEEDS_BORROWER_EMAIL,
      condition: isTypeDispatcher.value || isOwner.value,
      color: enums.feedbackType.WARNING,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.NEEDS_BORROWER_ACTION,
      condition: isTypeDispatcher.value || isValuer.value || isOwner.value,
      color: enums.feedbackType.WARNING,
      multiLine: false,
    },
    {
      key: enums.badgesStatus.APPOINTMENT_PENDING_CONFIRMATION,
      condition:
        (isTypeDispatcher.value || isValuer.value) &&
        !badges.value.includes(enums.badgesStatus.NEEDS_BORROWER_ACTION),
      color: enums.feedbackType.WARNING,
      multiLine: true,
    },
    {
      key: enums.badgesStatus.APPOINTMENT_CONFIRMED,
      condition:
        (isTypeDispatcher.value || isValuer.value || isOwner.value) &&
        !badges.value.includes(enums.badgesStatus.NEEDS_BORROWER_ACTION) &&
        !badges.value.includes(enums.badgesStatus.APPOINTMENT_PENDING_CONFIRMATION),
      color: enums.feedbackType.POSITIVE,
      multiLine: false,
    },
  ]

  return badgeConfigs
    .filter((config) => badges.value.includes(config.key) && config.condition)
    .map((config) => ({
      key: config.key,
      color: config.color,
      multiLine: config.multiLine,
      text: config.customText || t(`monolith.dashboard.badges.${config.key}`),
    }))
})
</script>
<style lang="scss" scoped>
.q-badge {
  word-wrap: normal;
  white-space: normal;
  word-break: normal;
}
</style>
